import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/shared/components/shadcn/badge';
import { cn } from '@/shared/utils';
import { RotateCcw } from 'lucide-react';
import { MAX_ER, MAX_FOLLOWER, MIN_ER, MIN_FOLLOWER } from '../../constants';
import { getPlatformLabel, getTimeRangeLabel } from '../../helpers/discovery.helpers';
import type { FilterConditions } from '@/entities/discovery';

interface Props {
  committedFilters?: FilterConditions;
  filters: FilterConditions;
  onResetFilter?: (filterKey: DiscoverySelectedFilterKey) => void;
}

export type DiscoverySelectedFilterKey = 'platform' | 'time-range' | 'followers' | 'er';

type DiscoverySelectedFilterItem = {
  changed: boolean;
  key: DiscoverySelectedFilterKey;
  label: string;
};

const MAX_ROWS = 2;
const CHIP_CLASS_NAME =
  'inline-flex items-center rounded-full border border-transparent bg-zinc-100 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-zinc-900';
const MORE_CHIP_CLASS_NAME =
  'inline-flex items-center rounded-full border border-transparent bg-zinc-100 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-zinc-600';

export function DiscoverySelectedFilters({ committedFilters, filters, onResetFilter }: Props) {
  const filterItems = useMemo(
    () => getDiscoverySelectedFilterItems(filters, committedFilters),
    [committedFilters, filters],
  );
  const visibleContainerRef = useRef<HTMLDivElement>(null);
  const measureContainerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(filterItems.length);

  useLayoutEffect(() => {
    const visibleContainer = visibleContainerRef.current;
    const measureContainer = measureContainerRef.current;

    if (!visibleContainer || !measureContainer) return;

    const measureVisibleCount = () => {
      const width = visibleContainer.clientWidth;
      if (width <= 0) return;

      const findRowCount = (count: number) => {
        measureContainer.replaceChildren();

        const wrap = document.createElement('div');
        wrap.className = 'flex flex-wrap gap-1.5';
        wrap.style.width = `${width}px`;

        filterItems.slice(0, count).forEach((item) => {
          const chip = document.createElement('span');
          chip.className = item.changed
            ? `${CHIP_CLASS_NAME} border-amber-200 bg-amber-50 text-amber-900`
            : CHIP_CLASS_NAME;
          chip.textContent = item.changed ? `${item.label} Reset` : item.label;
          wrap.appendChild(chip);
        });

        if (count < filterItems.length) {
          const moreChip = document.createElement('span');
          moreChip.className = MORE_CHIP_CLASS_NAME;
          moreChip.textContent = `+${filterItems.length - count} more`;
          wrap.appendChild(moreChip);
        }

        measureContainer.appendChild(wrap);

        const rowOffsets = Array.from(wrap.children).map(
          (child) => (child as HTMLElement).offsetTop,
        );
        return Array.from(new Set(rowOffsets)).length;
      };

      let nextVisibleCount = filterItems.length;

      for (let count = filterItems.length; count >= 0; count -= 1) {
        if (findRowCount(count) <= MAX_ROWS) {
          nextVisibleCount = count;
          break;
        }
      }

      setVisibleCount((prev) => (prev === nextVisibleCount ? prev : nextVisibleCount));
    };

    findInitialAndObserve();

    function findInitialAndObserve() {
      measureVisibleCount();
    }

    const resizeObserver = new ResizeObserver(() => {
      measureVisibleCount();
    });

    resizeObserver.observe(visibleContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [filterItems]);

  if (filterItems.length === 0) return null;

  const hiddenCount = Math.max(filterItems.length - visibleCount, 0);
  const visibleItems = filterItems.slice(0, visibleCount);

  return (
    <div className="relative">
      <div ref={visibleContainerRef} className="min-w-0">
        <div className="flex max-h-12 flex-wrap gap-1.5 overflow-hidden">
          {visibleItems.map((item) => (
            <Badge
              key={item.key}
              variant="secondary"
              className={cn(
                'gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
                item.changed && 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50/80',
              )}
            >
              <span>{item.label}</span>
              {item.changed && onResetFilter ? (
                <button
                  aria-label={`Reset ${item.label}`}
                  className="-mr-1 inline-flex size-4 items-center justify-center rounded-full text-amber-700 transition-colors hover:text-amber-950"
                  onClick={() => onResetFilter(item.key)}
                  type="button"
                >
                  <RotateCcw aria-hidden="true" className="size-3" />
                </button>
              ) : null}
            </Badge>
          ))}
          {hiddenCount > 0 ? (
            <Badge
              variant="secondary"
              className="text-muted-foreground rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
            >
              +{hiddenCount} more
            </Badge>
          ) : null}
        </div>
      </div>

      <div
        ref={measureContainerRef}
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 -z-10 opacity-0"
      />
    </div>
  );
}

function getDiscoverySelectedFilterItems(
  filters: FilterConditions,
  committedFilters?: FilterConditions,
): DiscoverySelectedFilterItem[] {
  const followerRange = getFollowerRange(filters);
  const erRange = getErRange(filters);
  const items: DiscoverySelectedFilterItem[] = [
    {
      changed: committedFilters ? filters.platform?.[0] !== committedFilters.platform?.[0] : false,
      key: 'platform',
      label: getPlatformLabel(filters),
    },
    {
      changed: committedFilters
        ? filters.start_date !== committedFilters.start_date ||
          filters.end_date !== committedFilters.end_date
        : false,
      key: 'time-range',
      label: getTimeRangeLabel(filters),
    },
  ];

  const committedFollowerRange = committedFilters
    ? getFollowerRange(committedFilters)
    : followerRange;
  const followerChanged =
    followerRange.min !== committedFollowerRange.min ||
    followerRange.max !== committedFollowerRange.max;

  if (followerRange.min !== MIN_FOLLOWER || followerRange.max !== MAX_FOLLOWER || followerChanged) {
    items.push({
      changed: followerChanged,
      key: 'followers',
      label: `Followers ${formatFollowerValue(followerRange.min)}-${formatFollowerValue(
        followerRange.max,
      )}`,
    });
  }

  const committedErRange = committedFilters ? getErRange(committedFilters) : erRange;
  const erChanged = erRange.min !== committedErRange.min || erRange.max !== committedErRange.max;

  if (erRange.min !== MIN_ER || erRange.max !== MAX_ER || erChanged) {
    items.push({
      changed: erChanged,
      key: 'er',
      label: `Average ER ${erRange.min}%-${erRange.max === MAX_ER ? `${MAX_ER}%+` : `${erRange.max}%`}`,
    });
  }

  return [...items.filter((item) => item.changed), ...items.filter((item) => !item.changed)];
}

function getFollowerRange(filters: FilterConditions) {
  return {
    min: filters.engagement?.followers_min ?? MIN_FOLLOWER,
    max: filters.engagement?.followers_max ?? MAX_FOLLOWER,
  };
}

function getErRange(filters: FilterConditions) {
  return {
    min: filters.engagement?.er_min ?? MIN_ER,
    max: filters.engagement?.er_max ?? MAX_ER,
  };
}

function formatFollowerValue(value: number) {
  if (value >= 1000000) {
    return `${value / 1000000}M`;
  }

  return `${value / 1000}K`;
}
